import { Link } from '@inertiajs/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type PaginatedData } from '@/types';

interface PaginationProps {
    data: PaginatedData<unknown>;
    preserveState?: boolean;
    preserveScroll?: boolean;
    className?: string;
}

const decodeLabel = (label: string) =>
    label
        .replace(/<[^>]*>/g, '')
        .replace(/&laquo;/g, '«')
        .replace(/&raquo;/g, '»')
        .replace(/&amp;/g, '&')
        .trim();

function buildVisiblePages(current: number, last: number, delta = 2): (number | '...')[] {
    const range: number[] = [];
    for (let i = Math.max(2, current - delta); i <= Math.min(last - 1, current + delta); i++) {
        range.push(i);
    }
    const pages: (number | '...')[] = [1];
    if (range[0] > 2) pages.push('...');
    pages.push(...range);
    if (range[range.length - 1] < last - 1) pages.push('...');
    if (last > 1) pages.push(last);
    return pages;
}

export function Pagination({
    data,
    preserveState = true,
    preserveScroll = true,
    className,
}: PaginationProps) {
    if (data.last_page <= 1) {
        return null;
    }

    const previousLink = data.links[0] ?? null;
    const nextLink = data.links[data.links.length - 1] ?? null;
    const pageLinks = data.links.slice(1, -1);
    const from = data.from ?? 0;
    const to = data.to ?? 0;

    const pageUrlMap: Record<number, string | null> = {};
    pageLinks.forEach((link) => {
        const label = decodeLabel(link.label);
        const pageNum = parseInt(label, 10);
        if (!isNaN(pageNum)) {
            pageUrlMap[pageNum] = link.url;
        }
    });

    const visiblePages = buildVisiblePages(data.current_page, data.last_page);

    return (
        <div className={cn('flex flex-col items-center gap-3 px-2 sm:flex-row sm:justify-between sm:gap-4', className)}>
            <div className="text-center text-sm text-muted-foreground sm:text-left">
                Mostrando <span className="font-medium">{from}</span> a <span className="font-medium">{to}</span> de{' '}
                <span className="font-medium">{data.total}</span> resultados
            </div>

            <nav aria-label="Paginação" className="flex flex-wrap items-center justify-center gap-1">
                {previousLink?.url ? (
                    <Link
                        href={previousLink.url}
                        preserveState={preserveState}
                        preserveScroll={preserveScroll}
                        className="inline-flex items-center justify-center rounded-md h-9 px-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="sr-only">Página anterior</span>
                    </Link>
                ) : (
                    <span className="inline-flex items-center justify-center rounded-md h-9 px-3 text-sm font-medium text-muted-foreground/50 cursor-not-allowed">
                        <ChevronLeft className="h-4 w-4" />
                        <span className="sr-only">Página anterior</span>
                    </span>
                )}

                {visiblePages.map((page, idx) => {
                    if (page === '...') {
                        return (
                            <span key={`ellipsis-${idx}`} className="inline-flex items-center justify-center h-9 w-9 text-sm">
                                ...
                            </span>
                        );
                    }

                    if (page === data.current_page) {
                        return (
                            <span
                                key={`active-${page}`}
                                className="inline-flex items-center justify-center rounded-md h-9 w-9 text-sm font-medium bg-primary text-primary-foreground shadow-sm"
                            >
                                {page}
                            </span>
                        );
                    }

                    const url = pageUrlMap[page];
                    if (!url) {
                        return (
                            <span
                                key={`disabled-${page}`}
                                className="inline-flex items-center justify-center rounded-md h-9 w-9 text-sm font-medium text-muted-foreground/50 cursor-not-allowed"
                            >
                                {page}
                            </span>
                        );
                    }

                    return (
                        <Link
                            key={`page-${page}`}
                            href={url}
                            preserveState={preserveState}
                            preserveScroll={preserveScroll}
                            className="inline-flex items-center justify-center rounded-md h-9 w-9 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                            {page}
                        </Link>
                    );
                })}

                {nextLink?.url ? (
                    <Link
                        href={nextLink.url}
                        preserveState={preserveState}
                        preserveScroll={preserveScroll}
                        className="inline-flex items-center justify-center rounded-md h-9 px-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                    >
                        <ChevronRight className="h-4 w-4" />
                        <span className="sr-only">Próxima página</span>
                    </Link>
                ) : (
                    <span className="inline-flex items-center justify-center rounded-md h-9 px-3 text-sm font-medium text-muted-foreground/50 cursor-not-allowed">
                        <ChevronRight className="h-4 w-4" />
                        <span className="sr-only">Próxima página</span>
                    </span>
                )}
            </nav>
        </div>
    );
}
